import { Badge, IconButton, IconFlagLine, AccessibleContent } from '@smarterservices/smarter-ui';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';

/**
 * SessionFlagIndicator Component
 * 
 * Displays a badge with the number of unseen flagging alerts for a session.
 * The badge only appears when there are unseen flags.
 * Clicking the badge opens the session in focus mode with the flags tab selected.
 * 
 * @component
 * @param {Object} props
 * @param {string} props.sessionSid - Session identifier to get unseen flag count for
 * 
 * @example
 * <SessionFlagIndicator sessionSid="session_123" />
 */
function SessionFlagIndicator({ sessionSid }) {
    // Subscribe only to the unseenCount for this specific session's flagging alerts
    const unseenCount = useSessionStore(
        (state) => state.flaggingAlerts?.[sessionSid]?.unseenCount || 0
    );

    // Get the openDrawer action from app store
    const openDrawer = useAppStore((state) => state.openDrawer);

    // Don't render anything if there are no unseen flags
    if (!unseenCount || unseenCount === 0) {
        return null;
    }

    const handleClick = () => {
        // Open focus drawer with flags tab pre-selected
        openDrawer('focus', sessionSid, { initialTab: 'flags' });
    };

    return (

        <Badge
            key={unseenCount} // Force remount when count changes to restart pulse animation
            count={unseenCount}
            pulse
            variant="danger"
            interaction="readonly"
            themeOverride={{
                size: '1rem',
                fontSize: '0.6rem',
                countOffset: '0rem',
                countPadding: '0.1rem'
            }}

            formatOutput={function (formattedCount) {
                return (
                    <AccessibleContent alt={`You have ${formattedCount} unseen flags`}>
                        {formattedCount}
                    </AccessibleContent>
                )
            }}
        >
            <IconButton
                renderIcon={IconFlagLine}
                screenReaderLabel="View unseen flags"
                withBorder={false}
                withBackground={false}
                onClick={handleClick}
            />
        </Badge>
    );
}

export default SessionFlagIndicator;
